import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { grey } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";

import fpCustomerLeadTimeService from "@plannel/services/fp/fp-customer-lead-time-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import BUCKET_CATALOG from "@plannel/pages/factory-plan/common/data/fp-bucket-options.json";
import FpGridSetting, {
  readHide, createSumAwareFormatter, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap, mergeChartRowsByGroupKey, buildChartDataAndSeries } from "@plannel/pages/factory-plan/common/chart";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old bucketUnit(planScope, viewId)의 실데이터가 planScope 축이 아니라 "시간 버킷 포함 여부" 축임이
// PlanResult 파일럿에서 이미 확인돼 정적 JSON 카탈로그(common/data/fp-bucket-options.json)로 전환돼
// 있다(fp-report-screen-guide.md §A-3). 이 화면은 PeriodSearchGroup을 쓰지 않지만(아래 참고) 그
// 카탈로그 자체는 재사용한다 — LT 단위 버킷(시/일/주/월)이라는 개념은 같고, 화면마다 새로 만들 이유가
// 없다. old가 BUCKET_WITH_HOUR 12종에서 'BUCKET_VARIABLE_HOUR' 1개만 걸러냈던 것과 동일하게 제외한다
// (새 백엔드 DayBucket enum에 BUCKET_VARIABLE_HOUR가 없어 선택하면 DayBucket.NULL로 빈 응답만 옴).
const BUCKET_OPTIONS = (BUCKET_CATALOG[BUCKET_CASE.WITH_HOUR] ?? [])
  .filter(b => b.value !== "BUCKET_VARIABLE_HOUR");
const DEFAULT_BUCKET_TYPE = (BUCKET_OPTIONS.find(b => b.defaultYn === "Y") ?? BUCKET_OPTIONS[0])?.value ?? "";

// 백엔드가 합계 행에 넣는 표식(old styleCallback의 summary-col 대응) — PlanResult와 동일한 방어적
// 처리. 실제로는 이 화면의 getUseGrouping()이 그리드 조회에선 항상 비어 있어(아래 buildBaseParams
// 참고) getSubSumLtDay()가 호출되지 않아 SUM 행이 내려오지 않는 경로지만(old도 동일), 백엔드가
// 나중에 합계 행을 붙여도 화면이 깨지지 않도록 그대로 유지한다.
const SUM_ROW = "SUM";

// 드릴다운 필터 순서 — 차트 재조회 파라미터 구성과 "이전 클릭과 같은가" 비교에 쓴다(old
// updateChartFromGrid의 column.startsWith 분기와 동일한 4단계). 이 화면의 크로스탭 그리드는
// old CustomerLeadTimeService가 항상 (itemParentGrpCd+itemCd) 단위로 최종 집계해서 내려주므로
// (필터로 범위만 좁아질 뿐 그레인은 항상 품목 단위) PlanResult처럼 차트 groupKey는 항상 "itemCd"
// 고정이다 — ResrcWipProfile처럼 클릭 레벨에 따라 groupKey를 바꿀 필요가 없다.
const HIERARCHY_FIELDS = ["customerCd", "itemParentGrpCd", "itemGrpCd", "itemCd"];

// 트리(행 그룹) 계층 — 고객 > 품목상위그룹 > 품목그룹 > (leaf)품목. old 컬럼정의(customerGroup/
// itemGroup 전부 `mergeRule`)를 근거로 Row Grouping을 선택했다(Tree Data 아님, §A-6b). 백엔드가
// 최종적으로 (itemParentGrpCd+itemCd) 단위로 집계하므로 품목이 leaf(각 행을 구분하는 마지막 값)다.
const GROUP_FIELDS = ["customerCd", "customerNm", "itemParentGrpCd", "itemParentGrpNm", "itemGrpCd", "itemGrpNm"];

const CUSTOMER_CD_COL_ID = "customerCdGrp";
const CUSTOMER_NM_COL_ID = `CUSTOMER_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_PARENT_GRP_CD_COL_ID = "itemParentGrpCdGrp";
const ITEM_PARENT_GRP_NM_COL_ID = `ITEM_PARENT_GRP_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_GRP_CD_COL_ID = "itemGrpCdGrp";
const ITEM_GRP_NM_COL_ID = `ITEM_GRP_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const CUSTOMER_NM_HIDE_KEY = "customerNmColsHide";
const ITEM_PARENT_GRP_NM_HIDE_KEY = "itemParentGrpNmColsHide";
const ITEM_GRP_NM_HIDE_KEY = "itemGrpNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";

const CustomerLeadTime = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });

  // old는 PeriodSearchGroup(계획 기간/캘린더 버킷)을 쓰지 않는다 — ltParam/bucketType은 LT(리드타임)
  // 값 자체의 버킷 단위(시/일/주/월)이고 조회 기간 개념이 아니다(fp-report-screen-guide.md와 별개
  // 축, 태스크 스펙에서도 명시). 그래서 이 3개는 화면 로컬 state로 직접 관리한다.
  const [ltParam, setLtParam] = useState(0);
  const [bucketType, setBucketType] = useState(DEFAULT_BUCKET_TYPE);
  const [isProduct, setIsProduct] = useState(true);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);

  // ltParam/bucketType/isProduct는 state지만 debouncedRetrieveRef가 감싸는 콜백이 최초 렌더
  // 시점의 클로저를 그대로 재사용하므로(PlanResult.js/ResrcWipProfile.js와 동일한 이유), 매 렌더
  // 최신값을 ref에 다시 담아 retrieveCustomerLeadTimes가 항상 이 ref를 통해서만 읽게 한다.
  const searchStateRef = useRef({ versionState, ltParam, bucketType, isProduct });
  searchStateRef.current = { versionState, ltParam, bucketType, isProduct };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveCustomerLeadTimes(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      getRowStyle: (params) => (params.data?.itemCd === SUM_ROW ? { backgroundColor: grey[100] } : null),
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sumAwareFormatter = createSumAwareFormatter(SUM_ROW, t);

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(customerCd/customerNm/itemParentGrpCd/itemParentGrpNm/
  // itemGrpCd/itemGrpNm/itemCd/itemNm/itemDisplayColor(hidden)/baseLt/planQty/avgLt)와 1:1.
  // 고객/품목상위그룹/품목그룹은 행 그룹(트리), 품목(itemCd)은 leaf라 평범한 field 컬럼 쌍이다.
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, CUSTOMER_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: CUSTOMER_CD_COL_ID, cdField: "customerCd", cdHeader: "FP_CUSTOMER_CD",
      nmColId: CUSTOMER_NM_COL_ID, nmField: "customerNm", nmHeader: "FP_CUSTOMER_NM",
      hideKey: CUSTOMER_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_PARENT_GRP_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: ITEM_PARENT_GRP_CD_COL_ID, cdField: "itemParentGrpCd", cdHeader: "FP_ITEM_PARENT_GRP_CD",
      nmColId: ITEM_PARENT_GRP_NM_COL_ID, nmField: "itemParentGrpNm", nmHeader: "FP_ITEM_PARENT_GRP_NM",
      hideKey: ITEM_PARENT_GRP_NM_HIDE_KEY, width: 155
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_GRP_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: ITEM_GRP_CD_COL_ID, cdField: "itemGrpCd", cdHeader: "FP_ITEM_GRP_CD",
      nmColId: ITEM_GRP_NM_COL_ID, nmField: "itemGrpNm", nmHeader: "FP_ITEM_GRP_NM",
      hideKey: ITEM_GRP_NM_HIDE_KEY, width: 150
    }),

    // leaf(품목) — 그룹이 아니라 실제 행의 값(PlanResult.js의 itemCd와 같은 역할).
    {
      headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 150, pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), reduxKey: ITEM_NM_HIDE_KEY, detailColumns: [ITEM_NM_COL_ID]
      }
    },
    {
      headerName: "ITEM_NM", field: "itemNm", colId: ITEM_NM_COL_ID, width: 150, pinned: "left", lockPinned: true,
      hide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter
    },

    // old headerText는 공백 1칸 + visible:false — 차트 색상(itemDisplayColor)에 쓰이므로 데이터는 유지.
    { headerName: " ", field: "itemDisplayColor", colId: "itemDisplayColor", hide: true },

    { headerName: "FP_BASE_LT_DAY", field: "baseLt", colId: "baseLt", width: 105, pinned: "left", lockPinned: true, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_PLANNED_QTY", field: "planQty", colId: "planQty", width: 100, pinned: "left", lockPinned: true, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_AVERAGE_LT_DAY", field: "avgLt", colId: "avgLt", width: 100, pinned: "left", lockPinned: true, type: ["numberColumn", "rightAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // old crosstabHeader는 PlanResult류의 "날짜_주차/월" 2단 그룹이 아니라 LT-bucket 헤더 1개당
  // 컬럼 1개(flat) — 예: "24 H\nD 1"(BUCKET_VARIABLE_DAY). old는 헤더에 '\n'이 있으면 RealGrid
  // 헤더를 2줄로 그렸지만(styleName:'multi-line-css'), new엔 그런 멀티라인 헤더 스타일이 아직
  // 없어 이 화면 하나만을 위해 새로 만들지 않는다(공용 파일 수정 최소화 원칙) — '\n'을 공백으로
  // 바꿔 한 줄로 단순화한다(정보 손실 없음, 표시만 한 줄). 값이 헤더로 그대로 쓰이는 동적 문자열이라
  // i18n 대상이 아니다(§A-6c와 동일 원칙).
  // 빨간 글씨(cellClassRules)는 old styleCallback의 `<header>_BASE_LT_FLAG === true` 판정 그대로 —
  // 실제 LT가 기준 LT(baseLt)를 초과한 버킷을 강조한다.
  const buildPivotColumnDefs = (crosstabHeader) => (crosstabHeader ?? []).map(header => ({
    headerName: header.includes("\n") ? header.replace(/\n/g, " ") : header,
    field: header, colId: header, width: 100,
    type: ["numberColumn", "rightAligned"],
    cellClassRules: {
      "red-text": (params) => params.data?.[`${header}_BASE_LT_FLAG`] === true
    }
  }));

  // §A-6c와 동일한 이유(조회마다 컬럼 구성 자체가 바뀜) — GridUtils.setColumnDefs가 되돌리는
  // "이전 조회의 컬럼 순서"를 crosstabHeader 순서로 교정한다.
  // old setCrossTabGridData — pivotData[i]/pivotbaseLtData[i]를 crosstabHeader[i] 필드 +
  // "<header>_BASE_LT_FLAG" 컴패니언 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      customerCd: row.customerCd, customerNm: row.customerNm,
      itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm,
      itemGrpCd: row.itemGrpCd, itemGrpNm: row.itemGrpNm,
      itemCd: row.itemCd, itemNm: row.itemNm,
      itemDisplayColor: row.itemDisplayColor,
      baseLt: row.baseLt, planQty: row.planQty, avgLt: row.avgLt
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
      flat[`${header}_BASE_LT_FLAG`] = row.pivotbaseLtData?.[index];
    });
    return flat;
  });

  // 조회/차트 공통 파라미터 — searchStateRef를 통해서만 읽는다(위 선언부 주석 참고, 클로저 staleness 방지).
  // useGrouping은 여기서 절대 넣지 않는다 — old도 그리드 조회(loadData)엔 이 키 자체를 안 보내
  // 백엔드의 getSubSumLtDay()(SUM 부분합 행 생성)가 타지 않는다. 차트 조회(loadChart)만 명시적으로
  // 'true'를 얹는다(아래 loadChart 참고) — old loadChartData와 동일.
  const buildBaseParams = () => {
    const { versionState: version, ltParam: lt, bucketType: bucket, isProduct: product } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      isProduct: !!product,
      ltParam: lt,
      bucketType: bucket
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartSeries([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — FilterStatusPanel의 6개 슬롯은 이름과 무관하게 항상
  // originLoc→destLoc→customer→item→workCenter→resource 순서로 렌더된다(공용 컴포넌트라 순서
  // 자체는 못 바꾼다, §금지사항). 이 화면은 그리드 고정 컬럼 순서가 고객>품목상위그룹>품목그룹>품목
  // 이라, 렌더 순서가 그 순서와 일치하도록 슬롯을 재배정한다: originLoc=고객(1번째로 렌더),
  // destLoc=품목상위그룹(2번째), customer=품목그룹(3번째), item=품목(4번째) — 라벨은 *Header 값으로
  // 직접 넣으므로 슬롯 이름 자체의 의미와 무관하게 화면에는 정확한 문구가 보인다(ResrcWipProfile.js와
  // 동일한 슬롯 재활용 원칙). workCenter/resource 슬롯은 이 화면엔 대응 레벨이 없어 비워둔다(값이
  // 없으면 FilterStatusPanel이 그 칩을 렌더하지 않음).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.customerCd ?? null, originLocCdHeader: t("FP_CUSTOMER_CD"),
      originLocNm: target.customerNm ?? null, originLocNmHeader: t("FP_CUSTOMER_NM"),
      destLocCd: target.itemParentGrpCd ?? null, destLocCdHeader: t("FP_ITEM_PARENT_GRP_CD"),
      destLocNm: target.itemParentGrpNm ?? null, destLocNmHeader: t("FP_ITEM_PARENT_GRP_NM"),
      customerCd: target.itemGrpCd ?? null, customerCdHeader: t("FP_ITEM_GRP_CD"),
      customerNm: target.itemGrpNm ?? null, customerNmHeader: t("FP_ITEM_GRP_NM"),
      itemCd: target.itemCd ?? null, itemCdHeader: t("FP_ITEM_CD"),
      itemNm: target.itemNm ?? null, itemNmHeader: t("ITEM_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. target = { customerCd, customerNm, itemParentGrpCd, ... }
  // (상위 레벨을 클릭하면 하위 코드는 없음 — old updateChartFromGrid와 동일).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;
    // SUM(부분합) 행은 차트 갱신 대상에서 제외한다(old의 SUM 셀 클릭 무시와 동일).
    if (target.itemCd === SUM_ROW) return;

    updateStatusBreadcrumb(target);

    const nextParams = {};
    HIERARCHY_FIELDS.forEach(field => { nextParams[field] = target[field] ?? ""; });
    const prev = prevChartParamsRef.current;
    if (prev && HIERARCHY_FIELDS.every(field => prev[field] === nextParams[field])) return;
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    // old loadChartData는 useGrouping:'true'를 명시적으로 보낸다 — 그리드 조회(buildBaseParams가
    // 안 보내는 값)와 유일하게 다른 점.
    const params = { ...buildBaseParams(), plantCds: versionState.plantCd, ...nextParams, useGrouping: "true" };

    // TODO(MIG-T2): [customerleadtime/CustomerLeadTime] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: customerCd/itemParentGrpCd/itemGrpCd/itemCd에 RealGrid autoFilter, 필터 변경
    //        (onFilteringChanged)이 현재 선택 행 기준으로 차트를 다시 그림
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다(fp-report-screen-guide.md §A-6e — PlanResult와 동일 사유,
    //        row grouping 쓰는 Report 화면 중 Set Filter 병용 화면 0건, 트리거 UI 선례 없음).
    //        백엔드도 이 엔드포인트는 changes(FilterParameter)를 안 읽으므로 2차엔 프론트 트리거
    //        UI뿐 아니라 백엔드 changes 지원도 함께 필요하다.
    fpCustomerLeadTimeService.getCustomerLeadTimes(params)
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const rows = (data ?? []).filter(r => r.itemCd !== SUM_ROW);

        if (isEmpty(rows) || isEmpty(crosstabHeader)) {
          setChartData([]);
          setChartSeries([]);
          setChartLabels([]);
          return;
        }

        // PivotChartComposed는 groupKey(itemCd) 값으로 범례를 자동 생성하고 그 목록을 data 행과
        // 인덱스로 짝짓는다 — itemCd가 중복되면 값이 어긋난다(PlanResult.js §A-9와 동일 주의).
        // 백엔드는 항상 (itemParentGrpCd+itemCd) 단위로 이미 집계해서 내려주지만, 방어적으로 한 번
        // 더 합산한다(common/chart.js — Report 화면 공통). y축 라벨은 old CustomerLeadTimeChart의
        // `(${transLangKey('QTY')})`를 그대로 이식(모든 series가 같은 groupName을 가져야 y축이
        // 하나로 합쳐진다).
        const merged = mergeChartRowsByGroupKey({ rows, groupKey: "itemCd", chartHeader: crosstabHeader, displayColorKey: "itemDisplayColor" });
        const { chartData, chartSeries: series } = buildChartDataAndSeries({
          merged, groupKey: "itemCd", chartHeader: crosstabHeader, displayColorKey: "itemDisplayColor",
          seriesOptions: { type: "bar", groupName: `(${t("QTY")})`, stackId: "customerLeadTime" }
        });

        setChartLabels(crosstabHeader);
        setChartData(chartData);
        setChartSeries(series);
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveCustomerLeadTimes = () => {
    const { versionState: version } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd };

    // 조회 중 AG Grid 내장 로딩 오버레이 표시(§0-8b 공통 컨벤션).
    gridRef.current?.api?.setGridOption("loading", true);

    fpCustomerLeadTimeService.getCustomerLeadTimes(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 고객 기준으로 차트를 그리고, 그리드의 현재 선택 셀도 그 행으로
        // 옮긴다(old gridView.setFocus()+setCurrent({itemIndex:0, column:'itemCd'}) 대응 —
        // PlanResult.js와 동일하게 트리의 rowIndex 0인 첫 고객 그룹 행 기준으로 잡는다).
        pendingFocusRef.current = { rowIndex: 0, colId: CUSTOMER_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ customerCd: rows[0].customerCd, customerNm: rows[0].customerNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const CUSTOMER_LEVEL_COL_IDS = new Set([CUSTOMER_CD_COL_ID, CUSTOMER_NM_COL_ID]);
  const ITEM_PARENT_GRP_LEVEL_COL_IDS = new Set([ITEM_PARENT_GRP_CD_COL_ID, ITEM_PARENT_GRP_NM_COL_ID]);
  const ITEM_GRP_LEVEL_COL_IDS = new Set([ITEM_GRP_CD_COL_ID, ITEM_GRP_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith('customer'|'itemParent'|'itemGrp')` 분기를 트리 표시 컬럼 colId 기준으로
  // 옮긴 것). groupHideOpenParents 때문에 그룹 행(접힌 대표 행)과 leaf(품목) 행 둘 다 다뤄야 한다
  // (fp-report-screen-guide.md §A-6b, PlanResult.js와 동일 패턴).
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (CUSTOMER_LEVEL_COL_IDS.has(colId)) {
      return { customerCd: row.customerCd, customerNm: row.customerNm };
    }
    if (ITEM_PARENT_GRP_LEVEL_COL_IDS.has(colId)) {
      return {
        customerCd: row.customerCd, customerNm: row.customerNm,
        itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm
      };
    }
    if (ITEM_GRP_LEVEL_COL_IDS.has(colId)) {
      return {
        customerCd: row.customerCd, customerNm: row.customerNm,
        itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm,
        itemGrpCd: row.itemGrpCd, itemGrpNm: row.itemGrpNm
      };
    }
    return row;
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 화면을 아우르는 별도 조회 버튼이 없다(Report 카테고리 전 화면 공통 정책, §A-9b) — old의
  // 글로벌 툴바 검색 버튼(versionSearchGroupRef.current.handleSubmit)은 new의 useViewStore/
  // globalButtons 배선 자체가 없어 그대로 옮길 수 없고, 가이드가 이미 정한 대체 방식(선택형
  // 조건은 값이 바뀌는 즉시 자동조회)을 그대로 따른다. bucketType(Select)/isProduct(Checkbox)는
  // 선택형이라 이 effect가 즉시 반응하고, ltParam(자유 숫자 입력)은 onBlur/Enter로만 수동 커밋한다
  // (아래 TextField 참고, old의 onParamKeyPress와 동일한 "Enter로만 반영" 성격).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    if (!version.planScope || !version.versionCd) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd, versionState.plantCd, bucketType, isProduct]);

  // PlanScope를 바꿔 하위(버전)까지 빈 값으로 리셋된 경우, 그리드·차트에 이전 계획범위의 조회
  // 결과가 그대로 남으면 안 된다 — versionCd가 비면 같이 비운다(PlanResult.js/ResrcWipProfile.js와
  // 동일 원칙).
  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  const handleLtParamChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setLtParam(0);
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return;
    setLtParam(Math.max(0, Math.trunc(parsed)));
  };

  const commitLtParam = () => debouncedRetrieveRef.current();

  // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
  // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
  const pivotConfig = useMemo(() => ({
    groupKey: "itemCd",
    chartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartSeries, chartLabels]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        {/* 자유 숫자 입력 — 선택형 조건과 달리 값이 바뀌는 즉시 재조회하지 않는다(§A-9b, old도
            onParamKeyPress로 Enter일 때만 loadData 호출). 포커스가 빠질 때도 같이 커밋한다. */}
        <TextField
          size="small"
          type="number"
          label={t("FP_LEAD_TIME")}
          value={ltParam}
          onChange={handleLtParamChange}
          onBlur={commitLtParam}
          onKeyDown={(e) => { if (e.key === "Enter") commitLtParam(); }}
          inputProps={{ min: 0 }}
          sx={{ ml: 1.5, minWidth: 120 }}
        />
        <FormControl size="small" sx={{ ml: 1.5, minWidth: 150 }}>
          <InputLabel id="customer-lead-time-bucket-label">{t("BUCKET")}</InputLabel>
          <Select
            labelId="customer-lead-time-bucket-label"
            label={t("BUCKET")}
            value={bucketType}
            onChange={(e) => setBucketType(e.target.value)}
          >
            {BUCKET_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{t(o.value)}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControlLabel
          sx={{ ml: 1.5 }}
          control={<Checkbox checked={isProduct} onChange={(e) => setIsProduct(e.target.checked)} />}
          label={t("FP_ITEM_CLASS_CD_P")}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old VerticalGridChartBox — 그리드 위/차트 아래, 명시적 비율 없이 CSS 기본(50/50에 가까움)
          이었다. SplitterContainer 기본값([3,2]=60/40)을 그대로 두면 그리드가 차트보다 넓어지므로
          PlanResult.js와 동일하게 1:1로 명시한다(§A-1c). */}
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-customer-lead-time-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* old CustomerLeadTimeChart.jsx는 plugins=[ChartLegendPlugin, ChartAxisTitlePlugin]로
              실제로 범례를 그렸다(PlanResult와 달리 이 화면은 범례가 있다 — plugins 배열을 직접
              확인해야 하는 이유, §A-7 경고 참고) — recharts 기본 Legend를 숨기지 않는다.
              TODO(MIG-T2): [customerleadtime/CustomerLeadTime] 차트 범례 클릭 토글(취소선) 미이관.
                old: ChartLegendPlugin — 범례 항목 클릭 시 해당 시리즈 숨김 + 취소선 스타일
                사유: recharts 기본 <Legend/>는 클릭 토글 기능이 없다(PivotChartComposed에 대응
                     훅 없음). 범례 표시 자체·막대 스태킹·툴팁·드릴다운 등 핵심 시각화는 정상 이관됨
                     — fp-report-screen-guide.md §A-7에서 Report 카테고리 공통 Tier2로 이미 확정된
                     항목과 동일 계열(화면 착수마다 재검토하지 않음). */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(CustomerLeadTime);
